/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, css } from 'aphrodite'
import { useStopwatch } from 'react-timer-hook'
import { Participant } from './Participant'
import PropTypes from 'prop-types'
import WidgetLiveControl from '../Widget/WidgetLiveControl'
import WidgetLiveTimeControl from '../Widget/WidgetLiveTimeControl'
import LivePending from '../../pages/LivePreview/LivePending'
import LiveContext from '../../components/Context/LiveContext'
import { COLOR_BLACK, COLOR_WHITE } from '../../lib/colors'
import dayjs from 'dayjs'
import AddMoreTimePopup from '../../modal/AddMoreTimePopup'
import { toast } from 'react-toastify'
import { Loader } from 'react-feather'

export const Room = ({ room, handleLogout, durationLive, setDurationLive }) => {
  const { t } = useTranslation('translation')
  const [participants, setParticipants] = useState([])
  const [isWatcherMuted, setWatcherMuted] = useState(false)
  const [isMakerMuted, setMakerMuted] = useState(false)
  const [isMakerOut, setIsMakerOut] = useState(false)
  // const [timeToAddTime, setTimeToAddTime] = useState(false)
  const [isConnected, setIsConnected] = useState(false)

  const { localDataTrack, toggleLivePreview } = useContext(LiveContext)

  const { seconds, minutes, hours, start } = useStopwatch({
    autoStart: true,
  })
  const hourTime = hours < 10 ? `0${hours}` : `${hours}`
  const minuteTime = minutes < 10 ? `0${minutes}` : `${minutes}`
  const secondTime = seconds < 10 ? `0${seconds}` : `${seconds}`

  useEffect(() => {
    const participantConnected = (participant) => {
      setParticipants((prevParticipants) => [...prevParticipants, participant])
      setIsConnected(true)
      setIsMakerOut(false)
      start()
    }

    const participantDisconnected = (participant) => {
      setParticipants((prevParticipants) =>
        prevParticipants.filter((p) => p !== participant)
      )
    }

    room.on('participantConnected', participantConnected)
    room.on('participantDisconnected', participantDisconnected)
    room.participants.forEach(participantConnected)
    return () => {
      room.off('participantConnected', participantConnected)
      room.off('participantDisconnected', participantDisconnected)
      room.localParticipant.audioTracks.forEach((publication) => {
        publication.track.stop()
        publication.unpublish()
      })
    }
  }, [room])

  useEffect(() => {
    return () => {
      room.localParticipant.audioTracks.forEach((publication) => {
        publication.track.stop()
        publication.unpublish()
      })
    }
  }, [])

  const handleParticipantMuted = () => {
    setMakerMuted((prev) => !prev)
  }

  const handleWatcherMuted = () => {
    setWatcherMuted((prev) => !prev)
  }

  const handleAddMoreTime = () => {
    localDataTrack.send('ping')
  }

  useEffect(() => {
    room.localParticipant.audioTracks.forEach((publication) => {
      isWatcherMuted ? publication.track.disable() : publication.track.enable()
    })
  }, [isWatcherMuted])

  useEffect(() => {
    if (hourTime >= durationLive) {
      localDataTrack.send('end')
      handleLogout()
    }
  }, [hourTime])

  const dateTimeRangeLive = JSON.parse(
    localStorage.getItem('dateTimeRangeLive')
  )
  useEffect(() => {
    if (
      dayjs(new Date()).format() >=
      dayjs(dateTimeRangeLive.end.value).subtract('5', 'minute').format()
    ) {
      toast(<AddMoreTimePopup onContinue={handleAddMoreTime} />, {
        autoClose: false,
        closeOnClick: false,
        draggable: true,
        closeButton: false,
        className: 'toast-container',
        toastClassName: 'toast',
      })
    }
  }, [minuteTime])

  const remoteParticipants = participants
    .filter((item, index) => index == 0)
    .map((participant) => (
      <Participant
        key={participant.sid}
        participant={participant}
        isMuted={isMakerMuted}
        setDurationLive={setDurationLive}
        handleLogout={handleLogout}
        setIsMakerOut={setIsMakerOut}
      />
    ))

  return room && isConnected ? (
    <div className={css(styles.room)}>
      {isMakerOut && (
        <div className={css(styles.makerOut)}>
          <Loader className={css(styles.animation)} />
          <p className={css(styles.makerOutText)}>{t('pages.room.makerOut')}</p>
        </div>
      )}
      <WidgetLiveTimeControl
        time={`${hourTime}:${minuteTime}:${secondTime}`}
        dynamicStyle={styles.liveTimeControl}
        onAddMoreTime={handleAddMoreTime}
      />
      <WidgetLiveControl
        dynamicStyle={styles.liveControl}
        onVolumeCliked={handleParticipantMuted}
        onMicClicked={handleWatcherMuted}
        onEndLive={() => {
          localDataTrack.send('end')
          handleLogout()
        }}
      />
      {remoteParticipants}
    </div>
  ) : (
    <LivePending />
  )
}

const translateKeyframes = {
  '0%': {
    transform: 'rotate(0deg)',
  },
  '100%': {
    transform: 'rotate(360deg)',
  },
}

const styles = StyleSheet.create({
  room: {
    width: '100%',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: COLOR_BLACK,
    height: '100%',
  },
  liveTimeControl: {
    position: 'fixed',
    top: '5vh',
    left: '50vw',
    transform: 'translateX(-50%)',
    zIndex: '10',
  },
  liveControl: {
    position: 'fixed',
    bottom: 10,
    zIndex: '10',
  },
  makerOut: {
    width: '100vw',
    height: '100vh',
    position: 'absolute',
    backgroundColor: COLOR_BLACK,
    opacity: 0.8,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: COLOR_WHITE,
  },
  makerOutText: {
    marginLeft: 10,
  },
  animation: {
    animationDuration: '2s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'linear',
    animationName: [translateKeyframes],
  },
})

Room.propTypes = {
  room: PropTypes.any,
  handleLogout: PropTypes.func,
  durationLive: PropTypes.number,
  setDurationLive: PropTypes.any,
}
